import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import {
	ChevronLeftIcon,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	FolderIcon,
	GearIcon,
	GiftIcon,
	SailboatIcon,
	SuitcaseIcon,
	TargetIcon,
} from '@treams/treams-icons';
import { Redirect, Route } from '@treams/treams-routes';
import { Avatar, Heading, Paragraph, Skeleton, Sidebar, Button } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import Drawer from './Drawer';
import { useHelicopter } from './HelicopterContext';
import { useModalLink } from './ModalNavigation';

const UserHeader = Skeleton(
	function UserHeader(props: { user: graphql.User }) {
		const { t } = useTranslation();
		const { localize } = useLocalize();
		const { isWorkspaceSuperuser } = useViewerRoles();
		const jobPosition = props.user.jobPositions[0];
		const jobTitle = localize(jobPosition?.jobTitle.title ?? [])?.text;

		const link = useModalLink(`/job-positions/${jobPosition?.id}`);

		return (
			<Flex alignItems="center" flexDirection="column">
				<Avatar user={props.user} mb={2} mr={2} />

				<Heading mb={0}>
					{props.user.firstName} {props.user.lastName}
				</Heading>
				{jobTitle && (
					<Paragraph>
						<Button link={link} title={jobTitle} type="plain" small textAlign="center" />
					</Paragraph>
				)}
				{props.user.mission.length > 0 && (
					<Paragraph mt={2} fontStyle="italic">
						{`"${props.user.mission}"`}
					</Paragraph>
				)}
				{isWorkspaceSuperuser && (
					<Button
						link={`/settings/employees/${props.user.id}`}
						title={t('sidebar.user.edit')}
						icon={<GearIcon />}
						mt={2}
						type="secondary"
						small
					/>
				)}
			</Flex>
		);
	},
	function UserHeaderSkeleton() {
		return (
			<Flex alignItems="center" flexDirection="column">
				<Avatar.Skeleton mb={2} />
				<Heading.Skeleton mb={2} />
				<Paragraph.Skeleton lines={1} />
			</Flex>
		);
	},
);

export default function UserSidebar(props: {
	visible?: boolean;
	userID: string;
	onClose?: () => void;
}) {
	const { t } = useTranslation();

	const [animated, setAnimated] = useState(false);

	useEffect(() => setAnimated(true), []);

	const query = graphql.useUserQuery({ variables: { id: props.userID } });

	const hasFeedback = useFeature('2021.feedback');
	const hasCompliments = useFeature('2021.compliments');
	const hasNotes = useFeature('2021.notes');
	const hasFeedbackCompetencies = useFeature('2021.feedback.competencies');

	const helicopter = useHelicopter();

	return (
		<Drawer position={props.visible && animated ? 'active' : 'right'} onClose={props.onClose}>
			{helicopter.enabled ? (
				<Button
					title={t('sidebar.manager.back')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					link="/manager"
					mb={4}
				/>
			) : (
				<Button
					title={t('sidebar.user.back')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					link="/"
					mb={4}
				/>
			)}
			{query.data && !query.loading ? (
				<UserHeader user={query.data.user as graphql.User} />
			) : (
				<UserHeader.Skeleton />
			)}
			<Sidebar.Spacing />
			{query.data && query.data.user.viewerCanViewJourney && (
				<>
					<Sidebar.Item
						icon={<SailboatIcon />}
						title={t('sidebar.peer.journey')}
						path={`/users/${props.userID}/journey`}
					/>
					<Route path="/users/:id" exact>
						{({ match }) => !!match && <Redirect to={`/users/${props.userID}/journey`} />}
					</Route>
				</>
			)}
			{query.data && query.data.user.viewerCanViewGoals && (
				<Sidebar.Item
					icon={<TargetIcon />}
					title={t('sidebar.peer.goals')}
					path={`/users/${props.userID}/goals`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewFeedback && hasFeedback && (
				<Sidebar.Item
					icon={<DialogueIcon />}
					title={t('sidebar.peer.feedback')}
					path={`/users/${props.userID}/feedback`}
				/>
			)}
			{query.data && !query.data.user.viewerCanViewFeedback && hasCompliments && (
				<Sidebar.Item
					icon={<GiftIcon />}
					title={t('sidebar.peer.compliments')}
					path={`/users/${props.userID}/feedback/compliments`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewFeedback && hasFeedbackCompetencies && (
				<Sidebar.Item
					icon={<SuitcaseIcon />}
					title={t('sidebar.peer.competencies')}
					path={`/users/${props.userID}/competencies`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewEvaluations && (
				<Sidebar.Item
					icon={<ConversationIcon />}
					title={t('sidebar.peer.conversations')}
					path={`/users/${props.userID}/conversations`}
				/>
			)}
			{hasNotes && (
				<Sidebar.Item
					icon={<DraftsIcon />}
					title={t('sidebar.peer.notes')}
					path={`/users/${props.userID}/notes`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewDocuments && (
				<Sidebar.Item
					icon={<FolderIcon />}
					title={t('sidebar.peer.personalFiles')}
					path={`/users/${props.userID}/personal-files`}
				/>
			)}
		</Drawer>
	);
}
